// §9.9 routing: validate and canonicalize the optional routing object. Every content-serving
// target (a 200 rewrite `to`, `fallback`, `notFound`) MUST reference a path present in
// `resources`; a build MUST reject one that does not. Redirect (3xx) `to` may be an in-site path
// or an absolute URL and is not required to exist.
import type { Routing } from "./types.ts";
import { BuildError } from "./normalize.ts";

const REDIRECT_STATUSES = new Set([301, 302, 307, 308]);

function isAbsoluteUrl(to: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(to);
}

/** Normalize an in-site path to `/`-rooted form; leaves absolute URLs untouched. */
function normPath(p: string): string {
  return p.startsWith("/") ? p : "/" + p;
}

/**
 * Validate routing against the resource set and return a canonical routing object with fields
 * omitted when unset, or undefined if `routing` is not set. Redirect array order is preserved
 * (significant, §3.7).
 */
export function canonicalizeRouting(routing: Routing | undefined, resourcePaths: Set<string>): Routing | undefined {
  if (routing === undefined) return undefined;

  const out: Routing = {};

  if (routing.redirects !== undefined) {
    if (!Array.isArray(routing.redirects)) throw new BuildError(`routing.redirects must be an array (§9.9)`);
    const redirects = routing.redirects.map((r) => {
      if (typeof r.status !== "number") throw new BuildError(`redirect status must be a number (§9.9)`);
      const isRewrite = r.status === 200;
      if (!isRewrite && !REDIRECT_STATUSES.has(r.status)) {
        throw new BuildError(`redirect status ${r.status} is not one of 200/301/302/307/308 (§9.9)`);
      }
      // `from` matches a path exactly or with a single trailing `*` splat.
      const fromRaw = r.from;
      const splat = fromRaw.endsWith("*");
      const fromBase = splat ? fromRaw.slice(0, -1) : fromRaw;
      const from = normPath(fromBase) + (splat ? "*" : "");
      let to = r.to;
      if (isRewrite) {
        // A rewrite serves content: `to` must reference an in-map resource.
        to = normPath(to);
        if (!resourcePaths.has(to)) {
          throw new BuildError(`routing rewrite target ${JSON.stringify(r.to)} is not in resources (§9.9)`);
        }
      } else if (!isAbsoluteUrl(to)) {
        to = normPath(to); // in-site redirect Location; existence not required
      }
      return { from, to, status: r.status };
    });
    if (redirects.length > 0) out.redirects = redirects;
  }

  if (routing.fallback !== undefined) {
    const fb = normPath(routing.fallback);
    if (!resourcePaths.has(fb)) {
      throw new BuildError(`routing.fallback ${JSON.stringify(routing.fallback)} is not in resources (§9.9)`);
    }
    out.fallback = fb;
  }

  if (routing.notFound !== undefined) {
    const nf = normPath(routing.notFound);
    if (!resourcePaths.has(nf)) {
      throw new BuildError(`routing.notFound ${JSON.stringify(routing.notFound)} is not in resources (§9.9)`);
    }
    out.notFound = nf;
  }

  // Omit entirely if nothing set.
  if (out.redirects === undefined && out.fallback === undefined && out.notFound === undefined) return undefined;
  return out;
}
